import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Header from '../../../components/Header';
import { tokens } from '../../../theme';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Axios from 'axios';
import Alert from '../../../components/alert/Alert';
import Backdrop from '@mui/material/Backdrop';
import getCookies from '../../../utils/getCookies';
import Loading from '../../../components/Loading';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import { Button, Divider, Collapse, TextField, Select, MenuItem, InputLabel, FormControl, FormControlLabel, Switch } from '@mui/material';
import { Link } from 'react-router-dom';
import { getAuthHeader } from '../../../utils/authToken';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

const TestResult = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const currentUser = getCookies("_auth_state");
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;
    const matches = useMediaQuery('(min-width:1500px)');
    const matches2 = useMediaQuery('(min-width:1200px)');
    
    // for 401 error
    const signOut = useSignOut();
    const navigate = useNavigate();
    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    }

    const courseData = useOutletContext().courseData;
    const [loading, setLoading] = React.useState(true);
    const [submissions, setSubmissions] = React.useState(null);
    const [studentNames, setStudentNames] = React.useState({});

    const [testInfo, setTestInfo] = React.useState(null);
    const [fullPoints, setFullPoints] = React.useState(0);
    const [frqMaxPoints, setFrqMaxPoints] = React.useState(0);
    const [hasFrq, setHasFrq] = React.useState(false);
    const [questionTypes, setQuestionTypes] = React.useState({ hasMcq: false, hasFrq: false, hasSa: false });
    const [hasApProfile, setHasApProfile] = React.useState(false);

    // AP Score Calculator state
    const [apCalcOpen, setApCalcOpen] = React.useState(false);
    const [apProfiles, setApProfiles] = React.useState(null);
    const [selectedProfile, setSelectedProfile] = React.useState('');
    const [mcqMultiplier, setMcqMultiplier] = React.useState('');
    const [frqWeightOverrides, setFrqWeightOverrides] = React.useState({});  // { saq: '3.1', leq: '3.5', ... }
    const [apCalcSaving, setApCalcSaving] = React.useState(false);

    const isAdmin = userRole === 'admin' || userRole === 'teacher';
    const isAdminOnly = userRole === 'admin';

    // Inline score editing state: { [submissionId]: { field: 'mcq'|'frq'|'ap', value: string } }
    const [editingScore, setEditingScore] = React.useState(null);

    const startEditScore = (submissionId, field, currentValue) => {
        setEditingScore({ submissionId, field, value: currentValue != null ? String(currentValue) : '' });
    };

    const cancelEditScore = () => setEditingScore(null);

    const saveEditScore = async () => {
        if (!editingScore) return;
        const { submissionId, field, value } = editingScore;
        const numValue = value === '' ? null : parseFloat(value);

        const body = {};
        if (field === 'mcq') body.score = numValue;
        if (field === 'frq') body.frq_score_override = numValue;
        if (field === 'ap') body.ap_score = numValue != null ? Math.round(numValue) : null;

        try {
            await Axios.patch(
                `${process.env.REACT_APP_URL}/api/v1/student-test-answers/${submissionId}/scores`,
                body,
                { headers: { Authorization: getAuthHeader(), 'Content-Type': 'application/json' } }
            );
            setSubmissions(prev => prev.map(s => {
                if (s.id !== submissionId) return s;
                const updated = { ...s };
                if (field === 'mcq') updated.score = numValue;
                if (field === 'frq') updated.frq_score_override = numValue;
                if (field === 'ap') updated.ap_score = numValue != null ? Math.round(numValue) : null;
                return updated;
            }));
            setEditingScore(null);
            setSuccessAlertMessage('Score updated');
            setSuccessAlertOpen(true);
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || 'Failed to update score');
            setErrorAlertOpen(true);
        }
    };

    const resetScoreOverride = async (submissionId, field, computedValue) => {
        const body = {};
        if (field === 'frq') body.frq_score_override = null;
        if (field === 'mcq') body.score = computedValue;
        if (field === 'ap') body.ap_score = null;

        try {
            await Axios.patch(
                `${process.env.REACT_APP_URL}/api/v1/student-test-answers/${submissionId}/scores`,
                body,
                { headers: { Authorization: getAuthHeader(), 'Content-Type': 'application/json' } }
            );
            setSubmissions(prev => prev.map(s => {
                if (s.id !== submissionId) return s;
                const updated = { ...s };
                if (field === 'frq') updated.frq_score_override = null;
                if (field === 'mcq') updated.score = computedValue;
                if (field === 'ap') updated.ap_score = null;
                return updated;
            }));
            setSuccessAlertMessage('Score reset to actual');
            setSuccessAlertOpen(true);
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || 'Failed to reset score');
            setErrorAlertOpen(true);
        }
    };

    const courseId = window.location.pathname.split('/')[2];
    const testId = window.location.pathname.split('/')[4];

    // for backdrop
    const [backdropOpen, setBackdropOpen] = React.useState(false);
    const handleBackdropClose = () => {
        setBackdropOpen(false);
    };

    // for alert
    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [errorAlertMessage, setErrorAlertMessage] = React.useState("");

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: colors.primary[300],
            color: theme.palette.common.white,
            fontSize: 14,
            fontWeight: "bold",
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 13,
        },
        border: '1px solid rgba(224, 224, 224, 1)',
    }));
      
    const StyledTableRow = styled(TableRow)(({ theme }) => ({
        '&:nth-of-type(odd)': {
            backgroundColor: colors.primary[750],
        },
        '&:nth-of-type(even)': {
            backgroundColor: colors.primary[750],
        },
    }));

    const handleRegrade = async (submissionId) => {
        try {
            const res = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/tests/${testId}/student-answers/${submissionId}/regrade-frq`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            setSuccessAlertMessage(res.data.message);
            setSuccessAlertOpen(true);
            // Refresh submissions after short delay
            setTimeout(() => window.location.reload(), 1500);
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Failed to re-grade");
            setErrorAlertOpen(true);
        }
    };

    React.useEffect(() => {

        async function fetchSubmissions() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/tests/${testId}/all-student-answers`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((response) => {
                const data = response.data;
                setSubmissions(data);

                const studentInfo = {};

                data.forEach((submission) => {
                    studentInfo[submission.student_id] = submission.student_name;
                });

                setStudentNames(studentInfo);
                setLoading(false);
            }).catch((error) => {
                if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                console.log(error.response?.data?.message);
            });
        }

        async function fetchTestInfo() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/tests/${testId}`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((response) => {        
                setTestInfo(response.data);
            }).catch((error) => {
                if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                console.log(error.response?.data?.message);
            });
        }

        fetchSubmissions();
        fetchTestInfo();
    }, []);

    // Fetch AP scoring profiles and check if course has ap_scoring_profile set
    React.useEffect(() => {
        Axios.get(`${process.env.REACT_APP_URL}/api/v1/ap-scoring-profiles`, {
            headers: { Authorization: getAuthHeader() },
        }).then(res => {
            if (res.data) {
                setApProfiles(res.data);
                const courseProfile = courseData?.ap_scoring_profile;
                if (courseProfile && res.data[courseProfile]) {
                    setHasApProfile(true);
                    if (!selectedProfile) {
                        setSelectedProfile(courseProfile);
                        const p = res.data[courseProfile];
                        setMcqMultiplier(String(p.mcq_multiplier));
                        // Initialize FRQ weight overrides from profile defaults
                        if (p.use_frq_type_weights && p.frq_weights) {
                            const wo = {};
                            for (const [key, val] of Object.entries(p.frq_weights)) wo[key] = String(val);
                            setFrqWeightOverrides(wo);
                        } else if (p.frq_flat_multiplier != null) {
                            setFrqWeightOverrides({ _flat: String(p.frq_flat_multiplier) });
                        }
                    }
                }
            }
        }).catch(() => {});
    }, [courseData]);

    React.useEffect(() => {
        if (testInfo) {
            let mcqCount = 0;
            let frqMax = 0;
            const allQuestions = [];

            if (testInfo.test_sections) {
                testInfo.test_sections.forEach(section => {
                    section.test_questions.forEach(q => allQuestions.push(q));
                });
            } else if (testInfo.test_questions) {
                testInfo.test_questions.forEach(q => allQuestions.push(q));
            }

            // Compute FRQ max excluding choice-group duplicates:
            // For each choice_group, only count the max_points once (pick the highest)
            const choiceGroupMaxes = {};
            allQuestions.forEach(q => {
                if (q.question_type === 'frq' || q.question_type === 'sa') {
                    if (q.choice_group) {
                        const key = `${q.choice_group}`;
                        choiceGroupMaxes[key] = Math.max(choiceGroupMaxes[key] || 0, q.max_points || 0);
                    } else {
                        frqMax += (q.max_points || 0);
                    }
                } else {
                    mcqCount++;
                }
            });
            // Add one entry per choice group
            Object.values(choiceGroupMaxes).forEach(v => { frqMax += v; });

            setFullPoints(mcqCount);
            setFrqMaxPoints(frqMax);

            const testHasFrq = testInfo.has_frq || frqMax > 0 ||
                (submissions && submissions.some(s => s.student_frq_answers && s.student_frq_answers.length > 0));
            setHasFrq(testHasFrq);

            // Detect question types for dynamic labels
            const types = { hasMcq: mcqCount > 0, hasFrq: false, hasSa: false };
            allQuestions.forEach(q => {
                if (q.question_type === 'frq') types.hasFrq = true;
                if (q.question_type === 'sa') types.hasSa = true;
            });
            setQuestionTypes(types);
        }
    }, [testInfo, submissions]);


    const parseStudentAnswers = (studentAnswers) => {
        if (testInfo && testInfo.test_sections) {
            return studentAnswers.split('@').map(section => section.split('|'));
        } else if (testInfo && !testInfo.test_sections && testInfo.test_questions) {
            return studentAnswers.split('|');
        }
    };

    const calculateSectionScore = (sectionIndex, studentAnswers, frqAnswers) => {
        let mcqScore = 0;
        let mcqCount = 0;
        let frqScore = 0;
        let frqMax = 0;

        const questions = testInfo.test_sections
            ? testInfo.test_sections[sectionIndex].test_questions
            : testInfo.test_questions;
        const answers = testInfo.test_sections
            ? studentAnswers[sectionIndex]
            : studentAnswers;

        const choiceGroupMaxes = {};
        questions.forEach((question, qIndex) => {
            if (question.question_type === 'frq' || question.question_type === 'sa') {
                // FRQ max from test structure, dedup choice groups
                if (question.choice_group) {
                    const key = `${question.choice_group}`;
                    choiceGroupMaxes[key] = Math.max(choiceGroupMaxes[key] || 0, question.max_points || 0);
                } else {
                    frqMax += question.max_points || 0;
                }
                // FRQ score from student's graded answers
                const qFrqAnswers = (frqAnswers || []).filter(a => a.question_id === question.id);
                qFrqAnswers.forEach(a => {
                    if (a.score != null) frqScore += a.score;
                });
            } else {
                mcqCount++;
                if (question.answer === answers?.[qIndex]) mcqScore++;
            }
        });
        Object.values(choiceGroupMaxes).forEach(v => frqMax += v);

        return { mcqScore, mcqCount, frqScore, frqMax, total: mcqScore + frqScore, totalMax: mcqCount + frqMax };
    };

    // ─── AP Course toggle ───
    const handleApToggle = async (checked) => {
        if (checked) {
            // Turning on — expand the calculator so admin picks a profile
            setHasApProfile(true);
            setApCalcOpen(true);
        } else {
            // Turning off — clear ap_scoring_profile on the course
            try {
                await Axios.patch(
                    `${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/ap-scoring-profile`,
                    { ap_scoring_profile: null },
                    { headers: { Authorization: getAuthHeader(), 'Content-Type': 'application/json' } }
                );
                setHasApProfile(false);
                setSelectedProfile('');
                setApCalcOpen(false);
                // Clear AP scores from local state
                setSubmissions(prev => prev.map(s => ({ ...s, ap_score: null, composite_score: null })));
                setSuccessAlertMessage('AP scoring disabled for this course');
                setSuccessAlertOpen(true);
            } catch (err) {
                setErrorAlertMessage(err.response?.data?.error || 'Failed to update');
                setErrorAlertOpen(true);
            }
        }
    };

    // ─── AP Score Calculator handlers ───
    const handleProfileChange = (profileName) => {
        setSelectedProfile(profileName);
        if (profileName && apProfiles?.[profileName]) {
            const p = apProfiles[profileName];
            setMcqMultiplier(String(p.mcq_multiplier));
            if (p.use_frq_type_weights && p.frq_weights) {
                const wo = {};
                for (const [key, val] of Object.entries(p.frq_weights)) wo[key] = String(val);
                setFrqWeightOverrides(wo);
            } else if (p.frq_flat_multiplier != null) {
                setFrqWeightOverrides({ _flat: String(p.frq_flat_multiplier) });
            } else {
                setFrqWeightOverrides({});
            }
        } else {
            setMcqMultiplier('');
            setFrqWeightOverrides({});
        }
    };

    const handleApCalcApplyAll = async () => {
        if (!selectedProfile) return;
        setApCalcSaving(true);
        try {
            const body = { profile_name: selectedProfile, course_id: courseData?.id };
            if (mcqMultiplier !== '') body.mcq_multiplier = parseFloat(mcqMultiplier);
            // Send per-type overrides or flat
            if (frqWeightOverrides._flat != null && frqWeightOverrides._flat !== '') {
                body.frq_multiplier = parseFloat(frqWeightOverrides._flat);
            } else {
                const wo = {};
                let hasOverride = false;
                for (const [key, val] of Object.entries(frqWeightOverrides)) {
                    if (key !== '_flat' && val !== '') { wo[key] = parseFloat(val); hasOverride = true; }
                }
                if (hasOverride) body.frq_weight_overrides = wo;
            }

            const res = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/tests/${testId}/recalculate-ap-all`,
                body,
                { headers: { Authorization: getAuthHeader(), 'Content-Type': 'application/json' } }
            );
            setHasApProfile(true);
            setSuccessAlertMessage(`AP scores updated for ${res.data.updated} student(s)`);
            setSuccessAlertOpen(true);
            // Update local submission data with new AP scores
            if (res.data.results) {
                setSubmissions(prev => prev.map(s => {
                    const updated = res.data.results.find(r => r.student_test_answer_id === s.id);
                    if (updated) {
                        return { ...s, ap_score: updated.ap_score, composite_score: updated.composite_score };
                    }
                    return s;
                }));
            }
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Failed to recalculate AP scores");
            setErrorAlertOpen(true);
        } finally {
            setApCalcSaving(false);
        }
    };

    const handleApCalcApplySingle = async (submissionId) => {
        if (!selectedProfile) return;
        try {
            const body = { profile_name: selectedProfile };
            if (mcqMultiplier !== '') body.mcq_multiplier = parseFloat(mcqMultiplier);
            if (frqWeightOverrides._flat != null && frqWeightOverrides._flat !== '') {
                body.frq_multiplier = parseFloat(frqWeightOverrides._flat);
            } else {
                const wo = {};
                let hasOverride = false;
                for (const [key, val] of Object.entries(frqWeightOverrides)) {
                    if (key !== '_flat' && val !== '') { wo[key] = parseFloat(val); hasOverride = true; }
                }
                if (hasOverride) body.frq_weight_overrides = wo;
            }

            const res = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/student-test-answers/${submissionId}/recalculate-ap`,
                body,
                { headers: { Authorization: getAuthHeader(), 'Content-Type': 'application/json' } }
            );
            // Update local state
            setSubmissions(prev => prev.map(s =>
                s.id === submissionId
                    ? { ...s, ap_score: res.data.ap_score, composite_score: res.data.composite_score }
                    : s
            ));
            setSuccessAlertMessage(`AP Score: ${res.data.ap_score} (composite: ${res.data.composite_score})`);
            setSuccessAlertOpen(true);
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Failed to recalculate");
            setErrorAlertOpen(true);
        }
    };

    // Inline editable score cell for admins
    const EditableScoreCell = ({ submissionId, field, displayValue, maxValue, computedValue, isOverridden, width }) => {
        const isEditing = editingScore?.submissionId === submissionId && editingScore?.field === field;
        // For FRQ: warn whenever override is set (going from N/A to any value matters)
        // For MCQ: warn when stored score differs from auto-graded score
        const showWarning = isOverridden && displayValue !== computedValue;

        if (!isAdminOnly) {
            return (
                <StyledTableCell style={{ width, fontSize: "32px", textAlign: "center" }}>
                    {displayValue != null ? `${displayValue}${maxValue != null ? ` / ${maxValue}` : ''}` : '—'}
                </StyledTableCell>
            );
        }

        if (isEditing) {
            return (
                <StyledTableCell style={{ width, textAlign: "center" }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <TextField
                            type="number"
                            size="small"
                            value={editingScore.value}
                            onChange={(e) => setEditingScore(prev => ({ ...prev, value: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveEditScore(); if (e.key === 'Escape') cancelEditScore(); }}
                            autoFocus
                            sx={{ width: 80 }}
                        />
                        {maxValue != null && <Typography variant="body2">/ {maxValue}</Typography>}
                        <IconButton size="small" onClick={saveEditScore} color="success"><CheckIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={cancelEditScore} color="error"><CloseIcon fontSize="small" /></IconButton>
                    </Box>
                </StyledTableCell>
            );
        }

        return (
            <StyledTableCell
                style={{ width, fontSize: "32px", textAlign: "center", cursor: 'pointer' }}
                onClick={() => startEditScore(submissionId, field, displayValue)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <span>{displayValue != null ? `${displayValue}${maxValue != null ? ` / ${maxValue}` : ''}` : '—'}</span>
                    {showWarning && (
                        <Tooltip title={computedValue != null && displayValue !== computedValue
                            ? `Actual: ${computedValue}${maxValue != null ? ` / ${maxValue}` : ''} — manually adjusted`
                            : 'Manually adjusted'
                        }>
                            <WarningAmberIcon sx={{ fontSize: 18, color: '#ed6c02' }} />
                        </Tooltip>
                    )}
                    {isOverridden && (
                        <Tooltip title="Reset to actual score">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); resetScoreOverride(submissionId, field, computedValue); }}>
                                <RestoreIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <EditIcon sx={{ fontSize: 14, opacity: 0.3 }} />
                </Box>
            </StyledTableCell>
        );
    };

    return (
        <Box m="20px" marginX={3} marginY={3} minHeight="90%">
            {loading || courseData === null || submissions === null ? (
                <Loading />
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        px: "18%",
                        ...!matches && {
                            px: "15%",
                        },
                        ...!matches2 && {
                            px: "4%",
                        },
                    }}
                >
                    <Header
                        title={
                            courseData.code !== null
                                ? courseData.code + ". " + courseData.course_title
                                : courseData.course_title
                        }
                        subtitle="A ONE Institute"
                    />
                    <Box
                        minWidth="20rem"
                        width="100%"
                        minHeight="80vh"
                        borderRadius="3px"
                        padding={3}
                        boxShadow="0px 0px 10px 10px rgba(0, 0, 0, 0.10)"
                        backgroundColor={colors.primary[600]}
                        display="flex"
                        flexDirection="column"
                        alignItems="left"
                        gap={3}
                        mb={3}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pb: 2, borderBottom: '0.1px solid', mb: 2 }}>
                            <Typography variant="h3" color={colors.primary[450]}>
                                Test Results
                            </Typography>
                            {isAdminOnly && apProfiles && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={hasApProfile}
                                            onChange={(e) => handleApToggle(e.target.checked)}
                                            color="success"
                                        />
                                    }
                                    label="AP Score"
                                    sx={{ mr: 0 }}
                                />
                            )}
                        </Box>

                        {/* AP Score Calculator Panel — admin only */}
                        {isAdminOnly && apProfiles && hasApProfile && (
                            <Box sx={{ mb: 2, border: `1px solid ${colors.primary[400]}`, borderRadius: 1 }}>
                                <Box
                                    sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        p: 1.5, cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                                    }}
                                    onClick={() => setApCalcOpen(!apCalcOpen)}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        AP Score Calculator
                                    </Typography>
                                    {apCalcOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </Box>
                                <Collapse in={apCalcOpen}>
                                    <Box sx={{ p: 2, pt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <FormControl size="small" sx={{ minWidth: 250 }}>
                                            <InputLabel>AP Scoring Profile</InputLabel>
                                            <Select
                                                value={selectedProfile}
                                                label="AP Scoring Profile"
                                                onChange={(e) => handleProfileChange(e.target.value)}
                                            >
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {Object.keys(apProfiles).map(name => (
                                                    <MenuItem key={name} value={name}>{name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {selectedProfile && apProfiles[selectedProfile] && (
                                            <>
                                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                    <TextField
                                                        label="MCQ Multiplier"
                                                        type="number"
                                                        size="small"
                                                        value={mcqMultiplier}
                                                        onChange={(e) => setMcqMultiplier(e.target.value)}
                                                        inputProps={{ step: 0.0001 }}
                                                        sx={{ width: 160 }}
                                                    />
                                                    {(() => {
                                                        const p = apProfiles[selectedProfile];
                                                        if (p.use_frq_type_weights && p.frq_weights) {
                                                            // Per-type editable fields
                                                            return Object.entries(p.frq_weights).map(([key]) => (
                                                                <TextField
                                                                    key={key}
                                                                    label={`FRQ: ${key}`}
                                                                    type="number"
                                                                    size="small"
                                                                    value={frqWeightOverrides[key] || ''}
                                                                    onChange={(e) => setFrqWeightOverrides(prev => ({ ...prev, [key]: e.target.value }))}
                                                                    inputProps={{ step: 0.0001 }}
                                                                    sx={{ width: 140 }}
                                                                />
                                                            ));
                                                        }
                                                        // Flat multiplier — single field
                                                        return (
                                                            <TextField
                                                                label="FRQ Multiplier"
                                                                type="number"
                                                                size="small"
                                                                value={frqWeightOverrides._flat || ''}
                                                                onChange={(e) => setFrqWeightOverrides({ _flat: e.target.value })}
                                                                inputProps={{ step: 0.0001 }}
                                                                sx={{ width: 160 }}
                                                            />
                                                        );
                                                    })()}
                                                </Box>

                                                {/* Conversion ranges */}
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {apProfiles[selectedProfile].conversion.map((r, i) => (
                                                        <Typography key={i} variant="caption" sx={{
                                                            px: 1, py: 0.5, borderRadius: 1,
                                                            backgroundColor: colors.primary[400],
                                                            color: colors.grey[100],
                                                        }}>
                                                            AP {r.score}: {r.min}-{r.max}
                                                        </Typography>
                                                    ))}
                                                </Box>

                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={handleApCalcApplyAll}
                                                        disabled={apCalcSaving}
                                                        sx={{
                                                            backgroundColor: colors.greenAccent[600],
                                                            ':hover': { backgroundColor: colors.greenAccent[700] },
                                                        }}
                                                    >
                                                        {apCalcSaving ? 'Calculating...' : 'Apply to All Students'}
                                                    </Button>
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                </Collapse>
                            </Box>
                        )}

                        {submissions && Object.keys(submissions).length > 0 ? (
                            Object.keys(studentNames).map((studentId) => (
                                <TableContainer key={studentId} component={Paper} sx={{ maxWidth: "100%" }}>
                                    <Table aria-label="simple table">
                                        <TableHead>
                                            {/* Show FRQ column for admins even on MCQ-only tests (for score overrides) */}
                                            {(() => {
                                                const showFrqCol = hasFrq || isAdminOnly;
                                                return (
                                            <StyledTableRow>
                                                <StyledTableCell style={{ width: "15%", textAlign: "center" }}>Name</StyledTableCell>
                                                {showFrqCol ? (
                                                    <>
                                                        <StyledTableCell style={{ width: "12%", textAlign: "center" }}>MCQ Score</StyledTableCell>
                                                        <StyledTableCell style={{ width: "12%", textAlign: "center" }}>
                                                            {questionTypes.hasFrq && questionTypes.hasSa ? 'FRQ + SA Score' : questionTypes.hasSa ? 'SA Score' : 'FRQ Score'}
                                                        </StyledTableCell>
                                                        <StyledTableCell style={{ width: "12%", textAlign: "center" }}>Total Score</StyledTableCell>
                                                        {hasApProfile && (
                                                            <StyledTableCell style={{ width: "10%", textAlign: "center" }}>AP Score</StyledTableCell>
                                                        )}
                                                    </>
                                                ) : hasApProfile ? (
                                                    <>
                                                        <StyledTableCell style={{ width: "20%", textAlign: "center" }}>Total Score</StyledTableCell>
                                                        <StyledTableCell style={{ width: "10%", textAlign: "center" }}>AP Score</StyledTableCell>
                                                    </>
                                                ) : (
                                                    <StyledTableCell style={{ width: "20%", textAlign: "center" }}>Total Score</StyledTableCell>
                                                )}
                                                {testInfo && testInfo.test_sections && (
                                                    <StyledTableCell style={{ width: "15%", textAlign: "center" }}>Section Scores</StyledTableCell>
                                                )}
                                                <StyledTableCell style={{ width: "15%", textAlign: "center" }}>Result</StyledTableCell>
                                            </StyledTableRow>
                                                );
                                            })()}
                                        </TableHead>
                                        <TableBody>
                                            {submissions.filter(submission => submission.student_id === parseInt(studentId)).map((submission, i) => {
                                                const studentAnswers = parseStudentAnswers(submission.student_answers);
                                                const isTestComplete = submission.score !== null && submission.score !== undefined;

                                                // FRQ score computation
                                                const frqAnswers = submission.student_frq_answers || [];
                                                const allFrqGraded = frqAnswers.length > 0 && frqAnswers.every(a => a.grading_status === 'completed');
                                                const anyFrqPending = frqAnswers.length > 0 && frqAnswers.some(a => a.grading_status === 'pending' || a.grading_status === 'grading');
                                                const frqScore = frqAnswers.reduce((sum, a) => sum + (a.grading_status === 'completed' ? (a.score || 0) : 0), 0);
                                                // Use student's actual FRQ max (handles choice groups correctly)
                                                const studentFrqMax = frqMaxPoints;

                                                // Compute actual MCQ score from answers vs correct answers
                                                let computedMcqScore = 0;
                                                if (testInfo && studentAnswers) {
                                                    const allQ = testInfo.test_sections
                                                        ? testInfo.test_sections.flatMap((s, si) =>
                                                            s.test_questions.map((q, qi) => ({ q, answer: studentAnswers[si]?.[qi] })))
                                                        : (testInfo.test_questions || []).map((q, qi) => ({ q, answer: studentAnswers[qi] }));
                                                    allQ.forEach(({ q, answer }) => {
                                                        if (q.question_type !== 'frq' && q.question_type !== 'sa' && q.answer === answer) {
                                                            computedMcqScore++;
                                                        }
                                                    });
                                                }
                                                const mcqIsOverridden = submission.score !== computedMcqScore;

                                                const showFrqCol = hasFrq || isAdminOnly;

                                                // Column count for "still taking" colspan
                                                let colSpanCount = 2; // Name + Result
                                                if (!showFrqCol && !hasApProfile) { colSpanCount = 3; }
                                                else if (!showFrqCol && hasApProfile) { colSpanCount = 4; }
                                                else {
                                                    colSpanCount += 3; // MCQ + FRQ + Total
                                                    if (hasApProfile) colSpanCount += 1;
                                                }

                                                return (
                                                    <StyledTableRow key={i}>
                                                        <StyledTableCell style={{ width: "15%", fontSize: "20px", textAlign: "center" }}>
                                                            {studentNames[submission.student_id]}
                                                        </StyledTableCell>
                                                        {isTestComplete ? (
                                                            <>
                                                                {(() => {
                                                                    const frqIsOverridden = submission.frq_score_override != null;
                                                                    // null = no override, no FRQ answers → show dash; 0 with override → show 0
                                                                    const frqDisplayScore = frqIsOverridden ? submission.frq_score_override : frqScore;
                                                                    const totalDisplay = (submission.score || 0) + (frqDisplayScore || 0);
                                                                    const totalMax = fullPoints + studentFrqMax;

                                                                    if (showFrqCol) return (
                                                                        <>
                                                                            <EditableScoreCell
                                                                                submissionId={submission.id}
                                                                                field="mcq"
                                                                                displayValue={submission.score}
                                                                                maxValue={fullPoints}
                                                                                computedValue={computedMcqScore}
                                                                                isOverridden={mcqIsOverridden}
                                                                                width="12%"
                                                                            />
                                                                            {anyFrqPending && !frqIsOverridden ? (
                                                                                <StyledTableCell style={{ width: "12%", textAlign: "center" }}>
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                                                        <CircularProgress size={20} />
                                                                                        <Typography variant="body1" sx={{ fontSize: "16px" }}>Grading...</Typography>
                                                                                    </Box>
                                                                                </StyledTableCell>
                                                                            ) : (
                                                                                <EditableScoreCell
                                                                                    submissionId={submission.id}
                                                                                    field="frq"
                                                                                    displayValue={frqDisplayScore}
                                                                                    maxValue={hasFrq ? studentFrqMax : null}
                                                                                    computedValue={frqScore}
                                                                                    isOverridden={frqIsOverridden}
                                                                                    width="12%"
                                                                                />
                                                                            )}
                                                                            <StyledTableCell style={{ width: "12%", fontSize: "32px", textAlign: "center" }}>
                                                                                {anyFrqPending && !frqIsOverridden ? (
                                                                                    <Typography variant="body1" sx={{ fontSize: "16px", color: colors.primary[450] }}>
                                                                                        {(submission.score || 0) + frqScore} / {totalMax}*
                                                                                    </Typography>
                                                                                ) : (
                                                                                    `${totalDisplay}${hasFrq ? ` / ${totalMax}` : ''}`
                                                                                )}
                                                                            </StyledTableCell>
                                                                            {hasApProfile && (
                                                                                <EditableScoreCell
                                                                                    submissionId={submission.id}
                                                                                    field="ap"
                                                                                    displayValue={submission.ap_score}
                                                                                    width="10%"
                                                                                />
                                                                            )}
                                                                        </>
                                                                    );

                                                                    if (hasApProfile) return (
                                                                        <>
                                                                            <EditableScoreCell
                                                                                submissionId={submission.id}
                                                                                field="mcq"
                                                                                displayValue={submission.score}
                                                                                maxValue={fullPoints}
                                                                                computedValue={computedMcqScore}
                                                                                isOverridden={mcqIsOverridden}
                                                                                width="20%"
                                                                            />
                                                                            <EditableScoreCell
                                                                                submissionId={submission.id}
                                                                                field="ap"
                                                                                displayValue={submission.ap_score}
                                                                                width="10%"
                                                                            />
                                                                        </>
                                                                    );

                                                                    return (
                                                                        <EditableScoreCell
                                                                            submissionId={submission.id}
                                                                            field="mcq"
                                                                            displayValue={submission.score}
                                                                            maxValue={fullPoints}
                                                                            width="20%"
                                                                        />
                                                                    );
                                                                })()}
                                                                {testInfo && testInfo.test_sections && (
                                                                    <StyledTableCell style={{ width: "15%" }}>
                                                                        {testInfo.test_sections.map((section, sectionIndex) => {
                                                                            const sectionScore = calculateSectionScore(sectionIndex, studentAnswers, frqAnswers);
                                                                            return (
                                                                                <Typography key={sectionIndex} variant="body1" sx={{ mb: 2, fontSize: "18px", textAlign: "center" }}>
                                                                                    Section {section.section_number}: <span style={{ marginLeft: 5 }}> {sectionScore.total} / {sectionScore.totalMax} </span>
                                                                                </Typography>
                                                                            );
                                                                        })}
                                                                    </StyledTableCell>
                                                                )}
                                                                <StyledTableCell style={{ width: "15%", fontSize: "20px", textAlign: "center" }}>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                                        <Link to={`/classrooms/${courseId}/test/${testId}/student/${submission.student_uuid}/submission`} style={{ textDecoration: 'none' }}>
                                                                            <Button
                                                                                variant="contained"
                                                                                sx={{
                                                                                    backgroundColor: colors.blueAccent[600],
                                                                                    color: 'white',
                                                                                    padding: '10px 20px',
                                                                                    borderRadius: '5px',
                                                                                    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)',
                                                                                    ':hover': {
                                                                                        backgroundColor: colors.blueAccent[700],
                                                                                    },
                                                                                }}
                                                                            >
                                                                                See Result
                                                                            </Button>
                                                                        </Link>
                                                                        {isAdminOnly && questionTypes.hasFrq && frqAnswers.length > 0 && (
                                                                            <Button
                                                                                variant="outlined"
                                                                                size="small"
                                                                                startIcon={<ReplayIcon />}
                                                                                onClick={() => handleRegrade(submission.id)}
                                                                                sx={{
                                                                                    color: colors.greenAccent[400],
                                                                                    borderColor: colors.greenAccent[400],
                                                                                    fontSize: '12px',
                                                                                    ':hover': {
                                                                                        borderColor: colors.greenAccent[300],
                                                                                        backgroundColor: 'rgba(76, 206, 172, 0.08)',
                                                                                    },
                                                                                }}
                                                                            >
                                                                                {questionTypes.hasFrq && questionTypes.hasSa ? 'Re-grade' : questionTypes.hasSa ? 'Re-grade SA' : 'Re-grade FRQ'}
                                                                            </Button>
                                                                        )}
                                                                    </Box>
                                                                </StyledTableCell>
                                                            </>
                                                        ) : (
                                                            <StyledTableCell colSpan={colSpanCount} style={{ fontSize: "40px", textAlign: "center" }}>
                                                                The student is still taking the test.
                                                            </StyledTableCell>
                                                        )}
                                                    </StyledTableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ))
                        ) : (
                            <Typography variant="h4" color={colors.primary[450]} sx={{ mx: 'auto', mt: '10rem' }} >
                                No submissions yet!
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
                vertical="bottom"
                horizontal="left"
            />
            <Backdrop
                sx={{ color: colors.primary[400], zIndex: 2147483646 }}
                open={backdropOpen}
                onClick={handleBackdropClose}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    )
}

export default TestResult;
